import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, VENDOR_META, DOC_META } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Server, FileText, Download, Eye, Loader2, Network, FileArchive, ShieldCheck, EyeOff,
} from "lucide-react";

const DOC_ORDER = ["hld", "lld", "asbuilt"];

export default function SharedProject() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [viewDoc, setViewDoc] = useState(null);
  const [viewContent, setViewContent] = useState("");
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shared/${token}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "This share link is invalid or has been disabled.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openDoc = async (doc) => {
    setViewDoc(doc); setLoadingDoc(true); setViewContent("");
    try {
      const res = await axios.get(`${API}/shared/${token}/documents/${doc.id}`);
      setViewContent(res.data.content);
    } catch { toast.error("Could not load document"); }
    finally { setLoadingDoc(false); }
  };

  const download = async (doc, format) => {
    try {
      const res = await axios.get(`${API}/shared/${token}/documents/${doc.id}/download?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.name}_${doc.doc_type}.${format === "docx" ? "docx" : "pdf"}`.replace(/[^a-zA-Z0-9._-]/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await axios.get(`${API}/shared/${token}/download-all?format=both`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.name}_documents.zip`.replace(/[^a-zA-Z0-9._-]/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("All documents downloaded (ZIP)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setDownloadingAll(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] text-[#8B949E] font-mono-jb flex items-center justify-center">Loading shared project...</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
        <div className="border border-white/10 bg-[#0B0C10] p-12 text-center max-w-md" data-testid="share-error">
          <EyeOff className="mx-auto text-[#FF3B30] mb-4" size={40} />
          <div className="font-mono-jb text-lg text-white mb-2">Link unavailable</div>
          <p className="text-[#8B949E] text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const docs = DOC_ORDER.filter((dt) => (data.documents || []).some((d) => d.doc_type === dt))
    .map((dt) => (data.documents || []).find((d) => d.doc_type === dt));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* top bar */}
      <header className="border-b border-white/10 bg-[#0B0C10] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo.png" alt="Soverenx Systems" className="h-10 object-contain" />
          <span className="flex items-center gap-2 text-[10px] font-mono-jb uppercase tracking-[0.2em] text-[#00FF41] border border-[#00FF41]/30 px-3 py-1.5">
            <ShieldCheck size={13} /> Read-only shared view
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="border-b border-white/10 pb-6 mb-8">
          <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">{data.client || "Shared Project"}</div>
          <h1 data-testid="shared-project-name" className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">{data.name}</h1>
          {data.description && <p className="text-[#8B949E] mt-3 max-w-3xl leading-relaxed">{data.description}</p>}
        </div>

        {/* devices */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Server className="text-[#00FF41]" size={18} />
            <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Devices ({data.devices?.length || 0})</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data.devices || []).map((d) => {
              const meta = VENDOR_META[d.vendor] || VENDOR_META.other;
              return (
                <div key={d.id} className="flex items-center gap-3 border border-white/10 px-3 py-2.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: meta.color }} />
                  <div className="min-w-0">
                    <div className="font-mono-jb text-sm text-white truncate">{d.name}</div>
                    <div className="text-[10px] uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* topology */}
        {data.has_topology && (
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Network className="text-[#00F0FF]" size={18} />
              <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Network Topology</h2>
            </div>
            <div className="border border-white/10 bg-white p-2 rounded-sm">
              <img data-testid="shared-topology-diagram" src={`${API}/shared/${token}/diagram.png`} alt="Network topology" className="w-full object-contain" />
            </div>
          </section>
        )}

        {/* documents */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <FileText className="text-[#EAE04A]" size={18} />
              <h2 className="font-mono-jb text-sm uppercase tracking-[0.15em] text-white">Documents</h2>
            </div>
            {docs.length > 0 && (
              <Button data-testid="shared-download-all" onClick={downloadAll} disabled={downloadingAll}
                className="rounded-sm bg-[#00FF41] text-black hover:bg-[#00FF41] font-mono-jb text-[11px] uppercase tracking-wider h-9 font-bold">
                {downloadingAll ? <Loader2 className="animate-spin" size={14} /> : <><FileArchive size={14} className="mr-1" /> Download All (ZIP)</>}
              </Button>
            )}
          </div>
          {docs.length === 0 ? (
            <div className="text-[#484F58] text-sm font-mono-jb py-6 text-center border border-dashed border-white/10">No documents shared yet.</div>
          ) : (
            <div className="space-y-3" data-testid="shared-documents-list">
              {docs.map((doc) => (
                <div key={doc.id} className="flex items-center gap-4 border border-white/10 p-4 hover:border-[#EAE04A]/40 transition-colors duration-200">
                  <div className="font-mono-jb font-extrabold text-2xl tracking-tighter text-[#EAE04A] w-24 shrink-0">{DOC_META[doc.doc_type].label}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-jb text-sm text-white">{doc.title}</div>
                    <div className="text-[10px] text-[#8B949E] uppercase tracking-wider mt-0.5">{DOC_META[doc.doc_type].full}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button data-testid={`shared-view-${doc.doc_type}`} onClick={() => openDoc(doc)} variant="outline"
                      className="rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-[#00F0FF] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    <Button data-testid={`shared-pdf-${doc.doc_type}`} onClick={() => download(doc, "pdf")}
                      className="rounded-sm bg-[#FF3B30]/90 text-white hover:bg-[#FF3B30] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                      <Download size={14} className="mr-1" /> PDF
                    </Button>
                    <Button data-testid={`shared-docx-${doc.doc_type}`} onClick={() => download(doc, "docx")}
                      className="rounded-sm bg-[#00F0FF] text-black hover:bg-[#00F0FF] font-mono-jb text-[11px] uppercase tracking-wider h-9">
                      <Download size={14} className="mr-1" /> Word
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* viewer */}
      <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center justify-between pr-8 gap-2">
              <span className="truncate">{viewDoc?.title}</span>
              {viewDoc && (
                <span className="flex gap-2 shrink-0">
                  <Button data-testid="shared-viewer-pdf" onClick={() => download(viewDoc, "pdf")} className="rounded-sm bg-[#FF3B30]/90 hover:bg-[#FF3B30] text-white h-8 text-[11px] font-mono-jb uppercase"><Download size={13} className="mr-1" />PDF</Button>
                  <Button data-testid="shared-viewer-docx" onClick={() => download(viewDoc, "docx")} className="rounded-sm bg-[#00F0FF] hover:bg-[#00F0FF] text-black h-8 text-[11px] font-mono-jb uppercase"><Download size={13} className="mr-1" />Word</Button>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto pr-2">
            {loadingDoc ? (
              <div className="font-mono-jb text-[#8B949E] py-10 text-center"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading document...</div>
            ) : (
              <div className="md-doc"><Markdown remarkPlugins={[remarkGfm]}>{viewContent}</Markdown></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
